import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getEventById } from '../../services/event.service';
import { Event, Ticket } from '../../types/event';
import { formatDate } from '../../utils/dateFormatter';
import { useTickets } from '../../hooks/useTickets';
import TicketList from './TicketList';
import TicketPurchaseModal from './TicketPurchaseModal';
import { motion } from 'framer-motion'; 
import { CalendarDaysIcon, MapPinIcon, TicketIcon, UserCircleIcon, SparklesIcon, InformationCircleIcon, ArrowDownIcon, HandThumbUpIcon } from '@heroicons/react/24/outline'; 
import './EventDescription.css';

interface EventDetailsProps {
  event?: Event;
}

const EventDetails: React.FC<EventDetailsProps> = ({ event: propEvent }) => {
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<Event | null>(propEvent || null);
  const [loading, setLoading] = useState<boolean>(!propEvent);
  const [error, setError] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  
  const { tickets, loading: ticketsLoading } = useTickets(event?.id || id || '');

  useEffect(() => {
    const fetchEvent = async () => {
      if (propEvent || !id) return;
      
      try {
        setLoading(true);
        const eventData = await getEventById(id);
        setEvent(eventData);
      } catch (err: any) {
        setError(err.message || 'Erreur lors du chargement de l\'événement');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id, propEvent]);

  const handleTicketSelect = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTicket(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-200px)]">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-yellow-500"></div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="text-center py-10 min-h-[calc(100vh-200px)] flex flex-col justify-center items-center">
        <InformationCircleIcon className="w-16 h-16 text-red-500 mb-4" />
        <p className="text-xl font-semibold text-red-400 mb-2">{error || 'Événement non trouvé'}</p>
        <p className="text-gray-400 mb-6">Désolé, nous n'avons pas pu charger les détails de cet événement.</p>
      </div>
    );
  }

  const isUpcoming = new Date(event.start_date) > new Date();

  const MotionInfoCard: React.FC<{icon: React.ReactNode, title: string, children: React.ReactNode, className?: string}> = ({icon, title, children, className}) => (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`bg-white/5 backdrop-blur-md border border-white/10 rounded-xl px-6 py-6 shadow-lg w-full md:w-[85%] mx-auto ${className}`}
    >
      <div className="flex flex-col items-center mb-6">
        {(title === 'Billets disponibles' || title === 'À propos de l\'événement') && (
          <HandThumbUpIcon className="w-10 h-10 text-yellow-400 mb-2 animate-bounce mx-auto" />
        )}
        <div className="flex items-center justify-center space-x-3 mb-1">
          {React.cloneElement(icon as React.ReactElement, { className: 'w-8 h-8 text-yellow-400' })}
          <h3 className="text-3xl font-bold text-white bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
            {title}
          </h3>
        </div>
        <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full"></div>
      </div>
      {children}
    </motion.div>
  );

  return (
    <div className="container mx-auto px-4 py-8 text-gray-100">
      {/* Section Billets */}
      <div className="w-full mb-8">
        <MotionInfoCard icon={<TicketIcon className="w-6 h-6" />} title="Billets disponibles">
          <div className="w-full">
            <TicketList 
              tickets={tickets} 
              loading={ticketsLoading} 
              onSelectTicket={handleTicketSelect}
              isEventPast={!isUpcoming}
            />
          </div>
        </MotionInfoCard>
        
        {event.description && (
          <div className="lg:w-[75%] mx-auto">
            <MotionInfoCard 
              icon={<InformationCircleIcon className="w-6 h-6" />} 
              title="À propos de l'événement"
              className="mt-8"
            >
              <div 
                className="prose prose-invert max-w-none event-description"
                dangerouslySetInnerHTML={{ __html: event.description }}
              />
            </MotionInfoCard>
          </div>
        )}
      </div>

      {/* Section Organisateur & Concours */}
      <div className="w-full lg:w-2/3 mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {event.organizer && (
            <MotionInfoCard icon={<UserCircleIcon className="w-6 h-6" />} title="Organisateur">
              <div className="flex items-center">
                {event.organizer.avatar_url ? (
                  <img 
                    src={event.organizer.avatar_url} 
                    alt={event.organizer.name || 'Organisateur'} 
                    className="w-16 h-16 rounded-full mr-4 object-cover border-2 border-yellow-500/50"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mr-4 border-2 border-yellow-500/50">
                    <span className="text-white font-medium text-2xl">
                      {event.organizer.name?.charAt(0).toUpperCase() || 'O'}
                    </span>
                  </div>
                )}
                <div>
                  <p className="font-semibold text-lg text-white">{event.organizer.name}</p>
                  {event.organizer.email && <p className="text-sm text-gray-400">{event.organizer.email}</p>}
                </div>
              </div>
            </MotionInfoCard>
          )}
          
          {event.contest && (
            <MotionInfoCard icon={<SparklesIcon className="w-6 h-6" />} title="Concours associé">
              <div className="flex items-center">
                {event.contest.logo_url ? (
                  <img 
                    src={event.contest.logo_url} 
                    alt={event.contest.name || 'Logo du concours'} 
                    className="w-16 h-16 object-contain rounded-md mr-4 p-1 bg-white/5"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-md bg-yellow-500/10 flex items-center justify-center mr-4">
                    <SparklesIcon className="w-8 h-8 text-yellow-400" />
                  </div>
                )}
                <div>
                  <p className="font-semibold text-lg text-white">{event.contest.name}</p>
                  {event.contest.description && (
                    <p className="text-sm text-gray-400 line-clamp-3">{event.contest.description}</p>
                  )}
                </div>
              </div>
            </MotionInfoCard>
          )}
        </div>
      </div>

      {/* Modal */}
      {selectedTicket && (
        <TicketPurchaseModal
          isOpen={isModalOpen}
          onClose={closeModal}
          ticket={selectedTicket}
          event={event}
          onChangeTicket={() => {
            // Garder la modale ouverte mais permettre de choisir un autre ticket
            setSelectedTicket(null);
          }}
        />
      )}
    </div>
  );
};

interface TicketPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: Ticket;
  event: Event;
  onChangeTicket?: () => void;
}

export default EventDetails;
